import React, { useState, useEffect } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CSpinner, CRow, CCol, CFormLabel,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { formatNotificationDate, formatNotificationDateSend } from 'src/utils/notificationDates';

/**
 * Модальне вікно швидкого перегляду сповіщення користувача.
 *
 * @param {boolean} open - чи відкрите вікно
 * @param {number|null} notificationId - ID сповіщення для завантаження
 * @param {object|null} [initialData] - сирий row з таблиці, якщо є — запит не робиться
 * @param {function} onClose - callback для закриття
 */
const UsersNotificationsModal = ({ open, notificationId, initialData, onClose }) => {
	const navigate = useNavigate();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open || !notificationId) {
			setData(null);
			return;
		}

		if (initialData) {
			setData(initialData);
			return;
		}

		setLoading(true);
		AxiosInstance.get(`/notifications/${notificationId}`)
			.then((res) => setData(res.data))
			.catch(() => setData(null))
			.finally(() => setLoading(false));
	}, [open, notificationId, initialData]);

	const handleOpenFull = () => {
		onClose();
		navigate(`/user-notifications/view/${notificationId}`);
	};

	return (
		<CModal visible={open} onClose={onClose} size="lg">
			<CModalHeader>
				<CModalTitle>
					{data ? `Сповіщення #${data.id} — ${data.title || '(без назви)'}` : 'Сповіщення користувача'}
				</CModalTitle>
			</CModalHeader>
			<CModalBody>
				{loading && (
					<div className="d-flex justify-content-center py-4">
						<CSpinner color="secondary" />
					</div>
				)}
				{!loading && data && (
					<CRow className="g-3">
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Логін</CFormLabel>
							<div>{data.users?.username || '—'}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Ім'я</CFormLabel>
							<div>{data.users?.firstName || '—'}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Тип</CFormLabel>
							<div>{data.type || '—'}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Статус</CFormLabel>
							<div>{data.status || '—'}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Дата створення</CFormLabel>
							<div>{formatNotificationDate(data.dateEntered)}</div>
						</CCol>
						<CCol md={6}>
							<CFormLabel className="fw-semibold">Дата відправлення</CFormLabel>
							{/* TODO [API]: dateSend приходить без коректного зсуву офсету — див. примітку в src/utils/notificationDates.js */}
							<div>{formatNotificationDateSend(data.dateSend)}</div>
						</CCol>
						{data.error && (
							<CCol md={6}>
								<CFormLabel className="fw-semibold">Помилка</CFormLabel>
								<div className="text-danger">{data.error}</div>
							</CCol>
						)}
						<CCol md={12}>
							<CFormLabel className="fw-semibold">Назва</CFormLabel>
							<div>{data.title || '—'}</div>
						</CCol>
						{data.body && (
							<CCol xs={12}>
								<CFormLabel className="fw-semibold">Текст повідомлення</CFormLabel>
								<div
									className="form-control"
									style={{ background: '#f8f9fa', minHeight: '80px', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}
								>
									{data.body}
								</div>
							</CCol>
						)}
					</CRow>
				)}
				{!loading && !data && notificationId && (
					<p className="text-danger">Не вдалося завантажити дані сповіщення.</p>
				)}
			</CModalBody>
			<CModalFooter>
				<CButton color="primary" onClick={handleOpenFull} title="Відкрити повну сторінку сповіщення">
					Відкрити повністю →
				</CButton>
				<CButton color="secondary" variant="outline" onClick={onClose} title="Закрити вікно швидкого перегляду">
					Закрити
				</CButton>
			</CModalFooter>
		</CModal>
	);
};

export default UsersNotificationsModal;
